import React, { FC, HTMLAttributes, useMemo } from "react";
import { EnclosureSp } from "components/front/layout/EnclosureSp";
import {
  DefaultValue,
  OrderCountLabel,
  RemainingDaysLabel,
  RemainingDaysValueProps,
  StyledBigValue,
  StyledSmallValue,
  StyledSmallValue2,
  SupportAmountLabel,
} from "./BusinessComponents";
import { GoalAmountArea, Rating } from "../atoms";
import dynamic from "next/dynamic";
import { EndingCountdownExpression } from "lib/project_util";
import { Props as SnsShareAreaProps } from "components/front/project/SnsShareArea";
import styled from "styled-components";
import BannerArea from "components/front/project/layout/BannerArea";
import { Banner, ProjectTotalization } from "interfaces/front/business";
import AnimefurusatoLpBanner from "components/front/project/banner/AnimefurusatoLpBanner";
import SimulatorBanner from "components/front/project/banner/SimulatorBanner";
import HometownTaxVideoBanner from "components/front/project/banner/HometownTaxVideoBanner";
import ProjectBanner from "components/front/project/banner/ProjectBanner";
import CorpfurusatoBanner from "components/front/project/banner/CorpfurusatoBanner";

const RemainingDaysValue = dynamic<RemainingDaysValueProps>(() => import("./BusinessComponents").then((m) => m.RemainingDaysValue), {
  ssr: false,
});
const SnsShareArea = dynamic<SnsShareAreaProps>(() => import("components/front/project/SnsShareArea"));

export type Props = HTMLAttributes<HTMLElement> & {
  slug: string;
  title: string;
  twitterHashtags: (string | null)[];
  supportAmount: string | undefined;
  supportAmountPool: string | undefined;
  rating?: number | null;
  goalAmount: string | undefined;
  summary: ProjectTotalization | undefined;
  remaining: EndingCountdownExpression | null;
  isHometownTax: boolean;
  isAboutHometownTax: boolean;
  isCorpHometownTax: boolean;
  isShopping: boolean;
  showsSupportAmount: boolean;
  onOpenVideo: () => void;
  banners: Banner[];
};
const QuantitativeSectionSp: FC<Props> = ({
  slug,
  title,
  twitterHashtags,
  supportAmount,
  supportAmountPool,
  rating,
  goalAmount,
  summary,
  remaining,
  isHometownTax,
  isAboutHometownTax,
  isCorpHometownTax,
  isShopping,
  showsSupportAmount,
  onOpenVideo,
  banners,
  ...props
}) => {
  const hasOrderCount = useMemo(() => typeof summary?.order_count === "number", [summary?.order_count]);
  const kamiari = (slug === "kamiari") ? "※この応援額は出雲市全体の寄附状況です。(随時更新します)" : ""
  return (
    <Root className="detail_visual sp" {...props}>
      <StyledEnclosureSp $shows={showsSupportAmount}>
        <AreaRoot>
          {(!isShopping &&
            <Row>
              <AmountArea>
                <LabelValueRow>
                  <SupportAmountLabel>現在の応援額</SupportAmountLabel>
                  <AmountValueArea>
                    <AmountRow $visible>
                      {supportAmount !== undefined ? (
                        <>
                          <StyledBigValue>{supportAmount}</StyledBigValue>
                          <StyledUnit $visible={!!supportAmount}>円</StyledUnit>
                        </>
                      ) : (
                        <DefaultValue>計算中</DefaultValue>
                      )}
                    </AmountRow>
                    <AmountRow $visible={!!supportAmountPool} $none={!!kamiari && !supportAmountPool}>
                      (<span style={{ marginRight: "5px" }}>未入金</span>
                      <StyledSmallValue>{supportAmountPool ?? "?"}</StyledSmallValue>
                      <span style={{ marginLeft: "5px" }}>円</span>)
                    </AmountRow>
                  </AmountValueArea>
                </LabelValueRow>
                <AmountRow $visible={!!kamiari} style={{ textAlign: "right", marginTop: "10px" }}>
                  <StyledSmallValue2>{kamiari}</StyledSmallValue2>
                </AmountRow>
              </AmountArea>
              {rating !== null && <StyledRatingArea rate={rating} />}
              {goalAmount && <GoalAmountArea amount={goalAmount} />}
            </Row>
          )}
          <FlexRow>
            <RowBlock>
              <OrderCountLabel>{isShopping ? "購入者数" : "応援者数"}</OrderCountLabel>
              <ValueArea>
                <StyledValue $exists={hasOrderCount}>{summary?.order_count ?? "計算中"}</StyledValue>
                <StyledUnit $visible={hasOrderCount}>人</StyledUnit>
              </ValueArea>
            </RowBlock>
            {slug != "kamiari" && (
              <RowBlock>
                <RemainingDaysLabel>残り日数</RemainingDaysLabel>
                <ValueArea>
                  <RemainingDaysValue remaining={remaining} />
                </ValueArea>
              </RowBlock>
            )}
          </FlexRow>
        </AreaRoot>
      </StyledEnclosureSp>
      <SnsShareArea slug={slug} title={title} twitterHashtags={twitterHashtags} style={{ margin: "20px 0" }} />
      <StyledBannerArea>
        {isHometownTax && <AnimefurusatoLpBanner />}
        {isHometownTax && <SimulatorBanner />}
        {isHometownTax && <HometownTaxVideoBanner onClick={onOpenVideo} />}
        {isCorpHometownTax && <CorpfurusatoBanner /> }
        {!isAboutHometownTax && banners.map((banner, index) => <ProjectBanner banner={banner} key={index} />)}
      </StyledBannerArea>
    </Root>
  );
};
export default QuantitativeSectionSp;

const StyledBannerArea = styled(BannerArea)`
  margin: 20px auto 40px;
`;
const Root = styled.section`
  margin-bottom: 20px;
`;
const AreaRoot = styled.div``;
const StyledEnclosureSp = styled(EnclosureSp)<{ $shows: boolean }>`
  display: ${({ $shows }) => ($shows ? "block" : "none")};
  margin: 20px 0;
`;
const Row = styled.div`
  margin-bottom: 20px;

  &:last-child {
    margin-bottom: 0;
  }
`;
const FlexRow = styled(Row)`
  display: flex;
  justify-content: flex-start;
  align-items: flex-start;
`;
const RowBlock = styled.div`
  margin-right: 40px;

  &:last-child {
    margin-right: 0;
  }
`;
const ValueArea = styled.p`
  margin: 13px 0 0 12px;
`;
const AmountArea = styled.div`
  margin-bottom: 20px;
`;
const LabelValueRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
`;

const AmountValueArea = styled.div`
  text-align: right;
`;
const AmountRow = styled.div<{ $visible: boolean, $none?: boolean }>`
  visibility: ${({ $visible }) => ($visible ? "visible" : "hidden")};
  display: ${({ $none = false }) => ($none ? "none" : "block")};
  margin-bottom: 10px;

  &:last-child {
    margin-bottom: 0;
  }
`;
const StyledRatingArea = styled(Rating)<{ rate?: number | null }>`
  visibility: ${({ rate }) => (rate !== undefined ? "visible" : "hidden")};
`;
const StyledValue = styled.span<{ $exists: boolean }>`
  font-style: italic;
  letter-spacing: 0.1em;
  @media screen and (max-width: 960px) {
    font-size: ${({ $exists }) => ($exists ? "22px" : "16px")};
    line-height: 18px;
  }
  @media screen and (min-width: 961px) and (max-width: 1280px) {
    font-size: ${({ $exists }) => ($exists ? "24px" : "16px")};
    line-height: 20px;
  }
  @media screen and (min-width: 1281px) {
    font-size: ${({ $exists }) => ($exists ? "26px" : "16px")};
    line-height: 22px;
  }
`;
const StyledUnit = styled.span<{ $visible: boolean }>`
  visibility: ${({ $visible }) => ($visible ? "visible" : "hidden")};
  margin-left: 5px;
  @media screen and (max-width: 960px) {
    font-size: 12px;
  }
  @media screen and (min-width: 961px) and (max-width: 1280px) {
    font-size: 14px;
  }
  @media screen and (min-width: 1281px) {
    font-size: 16px;
  }
`;
